import { useId, useState, type FormEvent } from 'react'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, useDirtyGuard } from '@/components/ui/dialog'
import { FormField } from '@/components/ui/form-field'
import { Input } from '@/components/ui/input'
import { errorText, toastFailure } from '@/lib/feedback'
import { toast } from '@/lib/toast'
import { CategorySelect } from '../components/CategorySelect'
import { deleteList } from '../api'
import { CrmApiError } from '../http'
import type { QueryBody } from '../query/types'
import { clearDraft, readDraft, writeDraft } from '../timeline/drafts'
import type { CrmCategoryRef, ListType } from '../types'
import { addListMembers, createListV2 } from './api'

const DRAFT_KEY = 'crm:lijst-opslaan'
const CHANGED = 'De selectie is veranderd; ververs de lijst en probeer opnieuw.'

interface Draft {
  name: string
  kind: CrmCategoryRef | null
  type: ListType
}

const isDraft = (value: unknown): value is Draft =>
  typeof value === 'object' && value !== null && typeof (value as Draft).name === 'string' && ((value as Draft).type === 'static' || (value as Draft).type === 'dynamic')

/**
 * "Opslaan als lijst" from the view menu of Contacten (design §5.7): the list on screen becomes a list. Dynamic
 * (the default) keeps the filter and the search term; static puts the people it shows now on it, with the total
 * on screen as `expected_total` (409 when the selection changed since). The draft survives Escape (ED-52).
 */
export function SaveAsListDialog({
  open,
  onOpenChange,
  query,
  total,
  onCreated,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The filter and search term on screen. */
  query: QueryBody | null
  /** How many people it shows now. */
  total: number | null
  onCreated: (listId: string) => void
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {open ? <SaveForm query={query} total={total} onOpenChange={onOpenChange} onCreated={onCreated} /> : null}
    </Dialog>
  )
}

function SaveForm({
  query,
  total,
  onOpenChange,
  onCreated,
}: {
  query: QueryBody | null
  total: number | null
  onOpenChange: (open: boolean) => void
  onCreated: (listId: string) => void
}) {
  const group = useId()
  const [draft, setDraft] = useState<Draft>(() => readDraft(DRAFT_KEY, isDraft) ?? { name: '', kind: null, type: 'dynamic' })
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  useDirtyGuard(draft.name.trim() !== '')

  function change(patch: Partial<Draft>) {
    const next = { ...draft, ...patch }
    setDraft(next)
    writeDraft(DRAFT_KEY, next)
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    const name = draft.name.trim()
    if (!name || busy || !query || (draft.type === 'static' && total === null)) return
    setBusy(true)
    setError(null)
    let createdId: string | null = null
    try {
      const dynamic = draft.type === 'dynamic'
      const created = await createListV2({ name, type: draft.type, kind_id: draft.kind?.id ?? null, ...(dynamic ? { filter: query } : {}) })
      createdId = created.id
      if (!dynamic) {
        await addListMembers(created.id, { query, expected_total: total ?? 0 })
      }
      clearDraft(DRAFT_KEY)
      onOpenChange(false)
      toast.success(`Lijst '${name}' aangemaakt.`)
      onCreated(created.id)
    } catch (caught) {
      // A static list that could not be filled is not left behind empty.
      if (createdId) {
        await deleteList(createdId).catch((problem: unknown) => toastFailure(problem, 'De lege lijst opruimen mislukt.'))
      }
      if (caught instanceof CrmApiError && caught.status === 409) toast.error(CHANGED)
      else setError(errorText(caught, 'Opslaan mislukt.'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Opslaan als lijst</DialogTitle>
        <DialogDescription>Maak van deze selectie een lijst: dynamisch volgt het filter, statisch bewaart de mensen die je nu ziet.</DialogDescription>
      </DialogHeader>
      <form onSubmit={(event) => void submit(event)} className="mt-4 flex flex-col gap-4">
        <FormField label="Naam" required error={error}>
          <Input maxLength={200} value={draft.name} onChange={(event) => change({ name: event.target.value })} autoFocus />
        </FormField>
        <div className="flex flex-col gap-1.5">
          <CategorySelect label="Soort" scope="lijst" value={draft.kind} onChange={(kind) => change({ kind })} />
        </div>
        <fieldset className="flex flex-col gap-1">
          <legend className="text-sm font-semibold">Type</legend>
          <label className="flex min-h-8 cursor-pointer items-center gap-2 text-sm max-md:min-h-11">
            <input type="radio" name={group} className="size-4 shrink-0 accent-primary" checked={draft.type === 'dynamic'} onChange={() => change({ type: 'dynamic' })} />
            Dynamisch – volgt het filter en de zoekterm
          </label>
          <label className="flex min-h-8 cursor-pointer items-center gap-2 text-sm max-md:min-h-11">
            <input type="radio" name={group} className="size-4 shrink-0 accent-primary" checked={draft.type === 'static'} onChange={() => change({ type: 'static' })} />
            {total === null ? 'Statisch – de mensen die je nu ziet' : `Statisch – de ${total} mensen die je nu ziet`}
          </label>
        </fieldset>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              clearDraft(DRAFT_KEY)
              onOpenChange(false)
            }}
          >
            Annuleren
          </Button>
          <Button type="submit" disabled={busy || !draft.name.trim() || !query || (draft.type === 'static' && total === null)}>
            {busy ? 'Bezig…' : 'Lijst aanmaken'}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  )
}
